import { NonIdealState, Tree } from '@blueprintjs/core';
import CollapsableButton from '../CollapsableButton';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ApplicationContext } from '../../../store/context';
import { MenuNode } from './menuNode';
import { useApplicationsNodes } from './useApplicationsNodes';
import { useManagerToolsNodes } from './useManagerToolsNodes';
import styles from './TreeMenu.module.css';
import { useTypedFlags } from '@axo/shared/services/feature-flags';
import { ACEFlags } from '../../../utils/ACEFlags';

interface Props {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

export const TreeMenu = (props: Props) => {
  const { state } = useContext(ApplicationContext);

  if (!state.marketCountry) {
    return <NonIdealState>Select a market country</NonIdealState>;
  }

  return <TreeMenuContent {...props} marketCountry={state.marketCountry} />;
};

function TreeMenuContent({
  collapsed,
  setCollapsed,
  marketCountry,
}: Props & { marketCountry: string }) {
  const { flag_disable_tree_view_buttons } = useTypedFlags(ACEFlags);

  const disable = useCallback(
    (node: MenuNode): MenuNode | undefined =>
      (flag_disable_tree_view_buttons ?? []).includes(node.id)
        ? undefined
        : {
            ...node,
            childNodes: node.childNodes
              ?.filter((node) => node !== undefined)
              .map(disable) as MenuNode[] | undefined,
          },
    [flag_disable_tree_view_buttons],
  );

  const applications = useApplicationsNodes(marketCountry);
  const managerTools = useManagerToolsNodes();

  const [expandedNodes, setExpandedNodes] = useState<MenuNode['id'][]>([
    applications[0].id,
  ]);

  const setExpanded = useCallback(
    (node: MenuNode): MenuNode => ({
      ...node,
      isExpanded: expandedNodes.includes(node.id),
      childNodes: node.childNodes?.filter(Boolean).map(setExpanded),
    }),
    [expandedNodes],
  );

  const nodes = useMemo<MenuNode[]>(
    () =>
      [...applications, ...managerTools]
        .map<MenuNode | undefined>(disable)
        .filter<MenuNode>((node) => node !== undefined)
        .map<MenuNode>(setExpanded),
    [applications, managerTools, disable, setExpanded],
  );

  const onNodeClick = (node: MenuNode) => {
    if (node.hasCaret) {
      if (node.isExpanded) {
        onNodeCollapse(node);
        return;
      }

      onNodeExpand(node);
      return;
    }

    node.nodeData?.onClick(node);
  };

  const onNodeCollapse = (node: MenuNode) =>
    setExpandedNodes((prevNodes) => prevNodes.filter((c) => c !== node.id));

  const onNodeExpand = (node: MenuNode) =>
    setExpandedNodes((prevNodes) => [...prevNodes, node.id]);

  return (
    <CollapsableButton
      collapsed={collapsed}
      icon="folder-open"
      onClick={() => setCollapsed(false)}
    >
      <Tree
        className={styles.tree}
        contents={nodes}
        onNodeClick={onNodeClick}
        onNodeCollapse={onNodeCollapse}
        onNodeExpand={onNodeExpand}
      />
    </CollapsableButton>
  );
}
